import {
  AdminCreateCustomerProduct,
  AdminDeactivateCustomerProduct,
  AdminFetchCustomerProducts,
  AdminUpdateCustomerProduct,
} from 'src/constants/actionTypes'
import type { CustomerProduct } from 'src/interfaces/customerProduct'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { CustomerProductState } from './types'

const getInitialState = (): CustomerProductState => {
  return {
    partnerId: null,
    loadedPartnerId: null,
    products: [],
    loading: false,
    error: null,
    saving: false,
  }
}

export const customerProductReducer = (
  state: CustomerProductState = getInitialState(),
  action: Action<unknown>,
): CustomerProductState => {
  switch (action.type) {
    case AdminFetchCustomerProducts.type.REQUEST: {
      const requestPayload = action.payload as { partnerId?: string } | undefined
      const requestedPartnerId = requestPayload?.partnerId ?? null
      const isSameCustomer = requestedPartnerId === state.partnerId

      return {
        ...state,
        partnerId: requestedPartnerId,
        // On a customer switch the previous customer's rows must never stay readable, because
        // feature gates derive from them. On a refetch of the SAME customer they are kept, so
        // the gate stays open and the table does not flash empty.
        products: isSameCustomer ? state.products : [],
        loadedPartnerId: isSameCustomer ? state.loadedPartnerId : null,
        loading: true,
        error: null,
      }
    }
    case AdminFetchCustomerProducts.type.SUCCESS: {
      const products = (action.payload as CustomerProduct[] | undefined) ?? []

      return {
        ...state,
        products,
        // The rows have settled for `partnerId` — this is what opens `CustomerLayout`'s gate.
        loadedPartnerId: state.partnerId,
        loading: false,
        error: null,
      }
    }
    case AdminFetchCustomerProducts.type.FAILED:
      return {
        ...state,
        // A failure settles the fetch too: the layout renders the tabs with an error banner
        // and every gate reads closed. `products` is cleared explicitly so that fail-closed
        // property is structural rather than dependent on REQUEST having run first.
        products: [],
        loadedPartnerId: state.partnerId,
        loading: false,
        error: resolveErrorMessage(action.payload, 'Failed to load products'),
      }

    // The mutations never write to `products` — the list is refetched on success, which is the
    // single source of truth. See the design doc's §10.
    case AdminCreateCustomerProduct.type.REQUEST:
    case AdminUpdateCustomerProduct.type.REQUEST:
    case AdminDeactivateCustomerProduct.type.REQUEST:
      return {
        ...state,
        saving: true,
      }
    case AdminCreateCustomerProduct.type.SUCCESS:
    case AdminCreateCustomerProduct.type.FAILED:
    case AdminUpdateCustomerProduct.type.SUCCESS:
    case AdminUpdateCustomerProduct.type.FAILED:
    case AdminDeactivateCustomerProduct.type.SUCCESS:
    case AdminDeactivateCustomerProduct.type.FAILED:
      return {
        ...state,
        saving: false,
      }

    default:
      return state
  }
}
